import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import type { ConditionStateItem } from '../create-segment-dialog';
import { X } from 'lucide-react';
import { useEffect } from 'react';

const buildScratchSchema = z.object({
  segmentName: z.string().trim().min(1, { message: 'Segment name cannot be empty' }),
  segmentDescription: z.string().optional()
});

export type BuildScratchFormValues = z.infer<typeof buildScratchSchema>;

interface BuildScratchStepProps {
  conditions: ConditionStateItem[];
  defaultValues?: Partial<BuildScratchFormValues>;
  onSubmit: (values: BuildScratchFormValues) => void;
  onAddCondition: () => void;
  onCancel: () => void;
  onDeleteCondition: (clientId: string) => void;
  isSubmitting: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
}

export function BuildScratchStep({
  conditions,
  defaultValues,
  onSubmit,
  onAddCondition,
  onCancel,
  onDeleteCondition,
  isSubmitting,
  onNameChange,
  onDescriptionChange
}: BuildScratchStepProps): React.ReactElement {
  const form = useForm<BuildScratchFormValues>({
    resolver: zodResolver(buildScratchSchema),
    defaultValues: {
      segmentName: defaultValues?.segmentName ?? '',
      segmentDescription: defaultValues?.segmentDescription ?? ''
    },
    mode: 'onChange'
  });

  const watchedName = form.watch('segmentName');
  const watchedDescription = form.watch('segmentDescription');

  useEffect(() => {
    onNameChange(watchedName);
  }, [watchedName, onNameChange]);

  useEffect(() => {
    onDescriptionChange(watchedDescription ?? '');
  }, [watchedDescription, onDescriptionChange]);

  const { isValid } = form.formState;

  return (
    <Form {...form}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Build Segment</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <FormField
            control={form.control}
            name="segmentName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Label</FormLabel>
                <FormControl>
                  <Input
                    placeholder="My Awesome Segment"
                    {...field}
                    className="bg-background border-border/40 focus-visible:ring-1 focus-visible:ring-border"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="segmentDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Optional: Add more details..."
                    {...field}
                    className="bg-background border-border/40 focus-visible:ring-1 focus-visible:ring-border"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <fieldset className="rounded-lg border p-4 border-border/40">
            <legend className="-ml-1 px-1 text-sm font-medium text-muted-foreground">
              Criteria
            </legend>

            {conditions.length === 0 ? (
              <div className="mt-3 text-center text-sm text-muted-foreground">
                No conditions added yet.
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {conditions.map((condition) => (
                  <div
                    key={condition.clientId}
                    className="flex items-center justify-between rounded-md border border-border/30 bg-background p-3 shadow-sm"
                  >
                    <span className="text-sm font-medium text-foreground">
                      {condition.displayName}
                    </span>
                    <div className="flex items-center space-x-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive/90"
                        onClick={() => {
                          onDeleteCondition(condition.clientId);
                        }}
                      >
                        <span className="sr-only">Delete condition</span>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4 w-full"
              onClick={onAddCondition}
            >
              Add Condition
            </Button>
          </fieldset>
        </div>

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Dynamic Segment'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
